import { defineMessage } from 'react-intl'

import {
  PROVIDER_SUPPLY_GROUP,
  SpeculativeSearchThresholds,
  TOOLKIT_TYPE,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS,
  WELLNESS_TOOLKIT_PROMOTION,
  WELLNESS_TOOLKIT_TYPE,
} from './types'
import { CENTER_OF_EXCELLENCE } from '../centerOfExcellence/types'
import { MANAGING_TEAMS_TOPIC_ID } from '../essentials/constants'

export const DEFAULT_SPECULATIVE_FILTER_THRESHOLDS: SpeculativeSearchThresholds = {
  [WELLNESS_TOOLKIT_TYPE.FAMILY_HUB]: {
    filters: {
      check: [
        {
          numProviders: {
            gte: 2,
          },
        },
      ],
    },
  },
  [WELLNESS_TOOLKIT_TYPE.INDIVIDUAL_COACHING]: {
    filters: {
      check: [
        {
          numProviders: {
            gte: 2,
          },
        },
      ],
    },
  },
  [WELLNESS_TOOLKIT_TYPE.PARENT_COACHING]: {
    filters: {
      check: [
        {
          numProviders: {
            gte: 2,
          },
        },
      ],
    },
  },
}

export const getToolkitCardTitle = (toolkitPromotion: TOOLKIT_TYPE, isLyraSelect: boolean) => {
  if (isLyraSelect) {
    switch (toolkitPromotion) {
      case WELLNESS_TOOLKIT_PROMOTION.WORK:
        return defineMessage({
          defaultMessage: 'Thrive at work',
          description: 'Work promotion card',
        })
      case WELLNESS_TOOLKIT_PROMOTION.PARENT:
        return defineMessage({
          defaultMessage: 'Parenting support',
          description: 'Parent promotion card',
        })
    }
  }
  switch (toolkitPromotion) {
    case WELLNESS_TOOLKIT_PROMOTION.WORK:
      return defineMessage({
        defaultMessage: 'Work',
        description: 'Work promotion card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.PURPOSE:
      return defineMessage({
        defaultMessage: 'Values',
        description: 'Values/purpose promotion card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS:
      return defineMessage({
        defaultMessage: 'Relationships',
        description: 'Relationships promotion card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.PARENT:
      return defineMessage({
        defaultMessage: 'Parenting',
        description: 'Parent promotion card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.FAMILY:
      return defineMessage({
        defaultMessage: 'Family support',
        description: 'Title of a card which links to a page dedicated to resources for family care',
      })
    case WELLNESS_TOOLKIT_PROMOTION.STRESS:
      return defineMessage({
        defaultMessage: 'Manage stress',
        description: 'Title of a card which links to a page dedicated to resources for managing stress',
      })
    case WELLNESS_TOOLKIT_PROMOTION.WELLBEING:
      return defineMessage({
        defaultMessage: 'Wellbeing',
        description: 'Title of a card which links to a page dedicated to resources for wellbeing',
      })
    case WELLNESS_TOOLKIT_PROMOTION.MINDFULNESS:
      return defineMessage({
        defaultMessage: 'Mindfulness',
        description: 'Title of a card which links to a page dedicated to resources for mindfulness',
      })
    case WELLNESS_TOOLKIT_PROMOTION.SELF_COMPASSION:
      return defineMessage({
        defaultMessage: 'Self-compassion',
        description: 'Title of a card which links to a page dedicated to resources for self-compassion',
      })
    case WELLNESS_TOOLKIT_PROMOTION.LIFE_TRANSITIONS:
      return defineMessage({
        defaultMessage: 'Life transitions',
        description: 'Title of a card which links to a page dedicated to resources for life transitions',
      })
    case WELLNESS_TOOLKIT_PROMOTION.CAREGIVER_STRESS:
      return defineMessage({
        defaultMessage: 'Caregiver support',
        description: 'Title of a card which links to a page dedicated to resources for caregiver support',
      })
    case WELLNESS_TOOLKIT_PROMOTION.WORKING_WITH_OTHERS:
      return defineMessage({
        defaultMessage: 'Work with others',
        description: 'Title of a card which links to a page dedicated to resources for work with others',
      })
    case WELLNESS_TOOLKIT_PROMOTION.TRAUMA:
      return defineMessage({
        defaultMessage: 'Trauma support',
        description: 'Title of a card which links to a page dedicated to resources for trauma support',
      })
    case CENTER_OF_EXCELLENCE.NEURODIVERSITY:
      return defineMessage({
        defaultMessage: 'Neurodiversity',
        description: 'Neurodiversity toolkit card title',
      })
    case CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES:
      return defineMessage({
        defaultMessage: 'Health challenges',
        description: 'Health challenges toolkit card title',
      })
    case CENTER_OF_EXCELLENCE.WOMENS_HEALTH:
      return defineMessage({
        defaultMessage: "Women's health",
        description: "Women's health toolkit card title",
      })
    case CENTER_OF_EXCELLENCE.MANAGER_HUB:
      return defineMessage({
        defaultMessage: 'Managing teams',
        description: 'Manager Hub card title',
      })
    case CENTER_OF_EXCELLENCE.PEDIATRICS:
      return defineMessage({
        defaultMessage: 'Pediatrics',
        description: 'Pediatrics toolkit card title',
      })
  }
}

export const getWellnessToolkitCardAccessibilityLabel = (toolkitPromotion: TOOLKIT_TYPE) => {
  switch (toolkitPromotion) {
    case WELLNESS_TOOLKIT_PROMOTION.WORK:
      return defineMessage({
        defaultMessage: 'Work illustration',
        description: 'Accessibility label for the work related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.PURPOSE:
      return defineMessage({
        defaultMessage: 'Values illustration',
        description: 'Accessibility label for the values/purpose wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS:
      return defineMessage({
        defaultMessage: 'Relationships illustration',
        description: 'Accessibility label for the relationships related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.PARENT:
      return defineMessage({
        defaultMessage: 'Parenting illustration',
        description: 'Accessibility label for the parenting related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.FAMILY:
      return defineMessage({
        defaultMessage: 'Family support illustration',
        description: 'Accessibility label for the family support related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.STRESS:
      return defineMessage({
        defaultMessage: 'Stress illustration',
        description: 'Accessibility label for the stress related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.WELLBEING:
      return defineMessage({
        defaultMessage: 'Wellbeing illustration',
        description: 'Accessibility label for the wellbeing related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.MINDFULNESS:
      return defineMessage({
        defaultMessage: 'Mindfulness illustration',
        description: 'Accessibility label for the mindfulness related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.SELF_COMPASSION:
      return defineMessage({
        defaultMessage: 'Self-compassion illustration',
        description: 'Accessibility label for the self-compassion related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.LIFE_TRANSITIONS:
      return defineMessage({
        defaultMessage: 'Life transitions illustration',
        description: 'Accessibility label for the life transitions related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.CAREGIVER_STRESS:
      return defineMessage({
        defaultMessage: 'Caregiver support illustration',
        description: 'Accessibility label for the caregiver stress related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.WORKING_WITH_OTHERS:
      return defineMessage({
        defaultMessage: 'Work with others illustration',
        description: 'Accessibility label for the work with others related wellness toolkit card',
      })
    case WELLNESS_TOOLKIT_PROMOTION.TRAUMA:
      return defineMessage({
        defaultMessage: 'Trauma support illustration',
        description: 'Accessibility label for the trauma support toolkit card',
      })
    case CENTER_OF_EXCELLENCE.NEURODIVERSITY:
      return defineMessage({
        defaultMessage: 'Neurodiversity illustration',
        description: 'Accessibility label for the neurodiversity toolkit card',
      })
    case CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES:
      return defineMessage({
        defaultMessage: 'Health challenges illustration',
        description: 'Accessibility label for the health challenges toolkit card',
      })
    case CENTER_OF_EXCELLENCE.WOMENS_HEALTH:
      return defineMessage({
        defaultMessage: 'Womens health illustration',
        description: 'Accessibility label for the womens challenges toolkit card',
      })
    case CENTER_OF_EXCELLENCE.MANAGER_HUB:
      return defineMessage({
        defaultMessage: 'Managing teams illustration',
        description: 'Accessibility label for the Manager Hub card',
      })
    case CENTER_OF_EXCELLENCE.PEDIATRICS:
      return defineMessage({
        defaultMessage: 'Pediatrics illustration',
        description: 'Accessibility label for the pediatrics toolkit card',
      })
  }
}

export const WELLNESS_GOAL_TO_WELLNESS_TOOLKIT_PROMOTION: Record<
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS,
  WELLNESS_TOOLKIT_PROMOTION
> = {
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.CAREGIVER_SUPPORT]: WELLNESS_TOOLKIT_PROMOTION.CAREGIVER_STRESS,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MINDFULNESS]: WELLNESS_TOOLKIT_PROMOTION.MINDFULNESS,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MANAGE_STRESS]: WELLNESS_TOOLKIT_PROMOTION.STRESS,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.PARENTING_SUPPORT]: WELLNESS_TOOLKIT_PROMOTION.PARENT,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.WORK_WITH_OTHERS]: WELLNESS_TOOLKIT_PROMOTION.WORKING_WITH_OTHERS,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.LIFE_TRANSITIONS]: WELLNESS_TOOLKIT_PROMOTION.LIFE_TRANSITIONS,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.PRACTICE_SELF_COMPASSION]: WELLNESS_TOOLKIT_PROMOTION.SELF_COMPASSION,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.THRIVE_AT_WORK]: WELLNESS_TOOLKIT_PROMOTION.WORK,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.STRENGTHEN_RELATIONSHIPS]: WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IMPROVE_YOUR_WELLBEING]: WELLNESS_TOOLKIT_PROMOTION.WELLBEING,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IDENTIFY_VALUES]: WELLNESS_TOOLKIT_PROMOTION.PURPOSE,
}

export const WELLNESS_GOALS_RANKING: Record<WELLNESS_GOALS_WORD_CLOUD_OPTIONS, number> = {
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.CAREGIVER_SUPPORT]: 1,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.PARENTING_SUPPORT]: 2,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.STRENGTHEN_RELATIONSHIPS]: 3,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.THRIVE_AT_WORK]: 4,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MANAGE_STRESS]: 5,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.WORK_WITH_OTHERS]: 6,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.LIFE_TRANSITIONS]: 7,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.PRACTICE_SELF_COMPASSION]: 8,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MINDFULNESS]: 9,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IMPROVE_YOUR_WELLBEING]: 10,
  [WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IDENTIFY_VALUES]: 11,
}

// word cloud terms ordered from general to specific
export const WELLNESS_GOALS_WORD_CLOUD_ORDER = [
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MINDFULNESS,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MANAGE_STRESS,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.PRACTICE_SELF_COMPASSION,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IMPROVE_YOUR_WELLBEING,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IDENTIFY_VALUES,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.THRIVE_AT_WORK,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.STRENGTHEN_RELATIONSHIPS,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.WORK_WITH_OTHERS,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.LIFE_TRANSITIONS,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.PARENTING_SUPPORT,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS.CAREGIVER_SUPPORT,
]

export const WFMH_ELIGIBLE_TOOLKITS = [
  WELLNESS_TOOLKIT_PROMOTION.WORK,
  WELLNESS_TOOLKIT_PROMOTION.PURPOSE,
  WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS,
  WELLNESS_TOOLKIT_PROMOTION.PARENT,
  WELLNESS_TOOLKIT_PROMOTION.FAMILY,
  WELLNESS_TOOLKIT_PROMOTION.TRAUMA,
  CENTER_OF_EXCELLENCE.NEURODIVERSITY,
  CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES,
  CENTER_OF_EXCELLENCE.WOMENS_HEALTH,
  CENTER_OF_EXCELLENCE.MANAGER_HUB,
]

export const WFMH_TOOLKIT_TO_PROVIDER_SUPPLY_GROUP: Partial<Record<TOOLKIT_TYPE, PROVIDER_SUPPLY_GROUP>> = {
  [WELLNESS_TOOLKIT_PROMOTION.WORK]: PROVIDER_SUPPLY_GROUP.INDIVIDUAL_COACHING,
  [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: PROVIDER_SUPPLY_GROUP.INDIVIDUAL_COACHING,
  [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: PROVIDER_SUPPLY_GROUP.INDIVIDUAL_COACHING,
  [WELLNESS_TOOLKIT_PROMOTION.PARENT]: PROVIDER_SUPPLY_GROUP.PARENT_COACHING,
  [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: PROVIDER_SUPPLY_GROUP.FAMILY_HUB,
  [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: PROVIDER_SUPPLY_GROUP.INDIVIDUAL_COACHING,
  [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: PROVIDER_SUPPLY_GROUP.INDIVIDUAL_COACHING,
  [CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES]: PROVIDER_SUPPLY_GROUP.INDIVIDUAL_COACHING,
}

/**
 * Represents the toolkit used to check individual coaching availability.
 * Work, Purpose, and Relationships toolkits all share the same availability conditions,
 * so we use WORK as the representative toolkit for these checks.
 */
export const INDIVIDUAL_COACHING_TOOLKIT = WELLNESS_TOOLKIT_PROMOTION.WORK

/**
 * Represents toolkits that SELECT users are eligible for.
 * Actual availability can depend on individual user properties
 * (i.e., user's age).
 */
export const SELECT_ELIGIBLE_TOOLKITS = [
  WELLNESS_TOOLKIT_PROMOTION.WORK,
  WELLNESS_TOOLKIT_PROMOTION.PURPOSE,
  WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS,
  WELLNESS_TOOLKIT_PROMOTION.PARENT,
  WELLNESS_TOOLKIT_PROMOTION.STRESS,
  WELLNESS_TOOLKIT_PROMOTION.WELLBEING,
  WELLNESS_TOOLKIT_PROMOTION.MINDFULNESS,
  WELLNESS_TOOLKIT_PROMOTION.SELF_COMPASSION,
  WELLNESS_TOOLKIT_PROMOTION.LIFE_TRANSITIONS,
  WELLNESS_TOOLKIT_PROMOTION.CAREGIVER_STRESS,
  WELLNESS_TOOLKIT_PROMOTION.WORKING_WITH_OTHERS,
  WELLNESS_TOOLKIT_PROMOTION.TRAUMA,
]

export const SELECT_ADULT_ONLY_TOOLKITS = [
  WELLNESS_TOOLKIT_PROMOTION.PARENT,
  WELLNESS_TOOLKIT_PROMOTION.CAREGIVER_STRESS,
]

/**
 * Toolkit promotions that Select minor/teen users are eligible for.
 * Excludes PARENT and CAREGIVER_STRESS which are only shown to adult users.
 */
export const SELECT_MINOR_ELIGIBLE_TOOLKITS = SELECT_ELIGIBLE_TOOLKITS.filter(
  (toolkit) => !SELECT_ADULT_ONLY_TOOLKITS.includes(toolkit),
)

export const WFMH_DEFAULT_TOOLKIT_PROMOTION_RANKING: Partial<Record<TOOLKIT_TYPE, number>> = {
  [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: 1,
  [WELLNESS_TOOLKIT_PROMOTION.WORK]: 2,
  [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: 3,
  [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: 4,
  [CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES]: 5,
  [CENTER_OF_EXCELLENCE.WOMENS_HEALTH]: 6,
  [WELLNESS_TOOLKIT_PROMOTION.PARENT]: 7,
  [CENTER_OF_EXCELLENCE.MANAGER_HUB]: 8,
  [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: 9,
  [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: 10,
}

export const WFMH_DEPRIORITIZED_NEURODIVERSITY_COE_PROMOTION_RANKING: Partial<Record<TOOLKIT_TYPE, number>> = {
  [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: 1,
  [WELLNESS_TOOLKIT_PROMOTION.WORK]: 2,
  [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: 3,
  [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: 4,
  [CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES]: 5,
  [CENTER_OF_EXCELLENCE.WOMENS_HEALTH]: 6,
  [WELLNESS_TOOLKIT_PROMOTION.PARENT]: 7,
  [CENTER_OF_EXCELLENCE.MANAGER_HUB]: 8,
  [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: 9,
  [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: 10,
}

export const WFMH_TRIGGERED_TOOLKIT_RANKING: Partial<Record<TOOLKIT_TYPE, number>> = {
  [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: 1,
  [CENTER_OF_EXCELLENCE.MANAGER_HUB]: 2,
  [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: 3,
  [CENTER_OF_EXCELLENCE.WOMENS_HEALTH]: 4,
  [WELLNESS_TOOLKIT_PROMOTION.PARENT]: 5,
  [CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES]: 6,
  [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: 7,
  [WELLNESS_TOOLKIT_PROMOTION.WORK]: 8,
  [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: 9,
  [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: 10,
}

export const TOOLKIT_TO_LIBRARY_TOPIC_ID: Partial<Record<TOOLKIT_TYPE, string>> = {
  [CENTER_OF_EXCELLENCE.MANAGER_HUB]: MANAGING_TEAMS_TOPIC_ID,
}
